import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  FileSignature, Send, Download, Eye, RefreshCw, Trash2, X,
  CheckCircle, Clock, AlertTriangle, FileText, Upload, Search, Plus, Zap,
  Mail, Phone,
} from 'lucide-react';

const STATUS_STYLES = {
  PENDING: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  SIGNED: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  REJECTED: 'bg-red-500/15 text-red-300 border-red-500/40',
};
const STATUS_ICONS = {
  PENDING: <Clock className="w-3.5 h-3.5" />,
  SIGNED: <CheckCircle className="w-3.5 h-3.5" />,
  REJECTED: <AlertTriangle className="w-3.5 h-3.5" />,
};

const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

/**
 * Iter131g — Client push permission banner.
 * If the browser is capable but the client hasn't granted push
 * permission yet, show a one-click "Turn on notifications" strip so
 * magic-link signing invites actually pop.
 */
const ClientPushBanner = () => {
  const supported = typeof window !== 'undefined' && 'Notification' in window;
  const [state, setState] = React.useState(supported ? Notification.permission : 'unsupported');
  const [busy, setBusy] = React.useState(false);
  if (!supported || state === 'granted' || state === 'denied' || state === 'unsupported') return null;
  const enable = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('nexus_token');
      const { enablePush } = await import('../services/firebase');
      const t = await enablePush({ backendUrl: BACKEND_URL, authToken: token });
      setState(Notification.permission);
      if (t) toast({ title: 'Notifications enabled', description: 'You\u2019ll get a push the moment a new document is ready to sign.' });
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded border border-blue-500/40 bg-blue-500/10 p-3 flex items-center gap-3" data-testid="client-push-banner">
      <FileSignature className="w-4 h-4 text-blue-300 flex-shrink-0" />
      <div className="text-xs md:text-sm text-blue-100 flex-1">
        Turn on browser notifications so signing invites reach you the moment your investigator sends one.
      </div>
      <Button size="sm" onClick={enable} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white h-8" data-testid="enable-push-btn">
        {busy ? 'Enabling\u2026' : 'Enable'}
      </Button>
    </div>
  );
};

/**
 * Iter131e — Client Documents module.
 * Admin: send docs to a specific client, view all, download.
 * Client: sees only docs addressed to them, review & E-sign.
 */
const ClientDocuments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = user?.role === 'main_hub' || user?.role === 'department_admin';
  const isClient = user?.role === 'client';

  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openDoc, setOpenDoc] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (status !== 'all') qs.set('status', status);
      const [dRes, cRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/client-documents?${qs}`, { headers }),
        isAdmin ? fetch(`${BACKEND_URL}/api/clients`, { headers }) : Promise.resolve(null),
      ]);
      if (dRes.ok) setDocs(await dRes.json());
      if (cRes && cRes.ok) setClients(await cRes.json());
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [headers, status, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const filteredDocs = docs.filter((d) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (d.title || '').toLowerCase().includes(s)
      || (d.client_name || '').toLowerCase().includes(s)
      || (d.id || '').toLowerCase().includes(s);
  });

  const stats = docs.reduce((acc, d) => {
    acc.total++; acc[d.status] = (acc[d.status] || 0) + 1; return acc;
  }, { total: 0 });

  return (
    <div className="space-y-6" data-testid="client-documents-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center rounded-lg" style={{ background: '#1D4ED8', boxShadow: '0 6px 14px -4px rgba(59,130,246,0.55)' }}>
            <FileSignature className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="nx-eyebrow">{isClient ? 'Your' : 'Admin ·'} Documents</div>
            <h1 className="nx-h1 mt-1">Client Document Center</h1>
            <p className="nx-body mt-1">
              {isClient
                ? 'Review and E-sign every document your investigation team has sent you.'
                : 'Send any document to a client for signature. Track status and download the signed copy.'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => navigate('/client-documents/templates')}
                      className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10" data-testid="templates-btn">
                <FileText className="w-4 h-4 mr-2" /> Templates
              </Button>
              <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-document-btn">
                <Send className="w-4 h-4 mr-2" /> Send Document
              </Button>
            </>
          )}
        </div>
      </div>

      {isClient && <ClientPushBanner />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[['total', 'Total', 'text-[#F8FAFC]'], ['PENDING', 'Pending', 'text-amber-300'],
          ['SIGNED', 'Signed', 'text-emerald-300'], ['REJECTED', 'Rejected', 'text-red-300']].map(([k, label, color]) => (
          <Card key={k} className="bg-[#131A2A] border-[#334155]"><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#94A3B8]">{label}</div>
            <div className={`text-3xl font-semibold mt-2 ${color}`}>{stats[k] || 0}</div>
          </CardContent></Card>
        ))}
      </div>

      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <CardTitle className="text-[#F8FAFC] text-base">Documents</CardTitle>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search"
                     className="pl-7 bg-[#0B0F19] border-[#334155] text-[#F8FAFC] h-9 w-[200px]"
                     data-testid="doc-search-input" />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC] h-9 w-[140px]" data-testid="doc-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131A2A] border-[#334155]">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="SIGNED">Signed</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="documents-table">
              <thead className="bg-[#0B0F19] text-[#94A3B8] uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Title</th>
                  {isAdmin && <th className="text-left px-4 py-3">Client</th>}
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Sent</th>
                  <th className="text-left px-4 py-3">Signed</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map((d) => (
                  <tr key={d.id} className="border-t border-[#334155] hover:bg-[#182136]" data-testid={`doc-row-${d.id}`}>
                    <td className="px-4 py-3">
                      <div className="text-[#F8FAFC] font-medium">{d.title}</div>
                      <div className="text-[10px] text-[#94A3B8] font-mono mt-0.5">{d.id}</div>
                    </td>
                    {isAdmin && <td className="px-4 py-3 text-[#CBD5E1]">{d.client_name || '—'}</td>}
                    <td className="px-4 py-3">
                      <Badge className={`${STATUS_STYLES[d.status] || 'bg-slate-500/15 text-slate-300 border-slate-500/40'} inline-flex items-center gap-1`}>
                        {STATUS_ICONS[d.status] || null} {d.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-[#CBD5E1]">{fmt(d.created_at)}</td>
                    <td className="px-4 py-3 text-[#CBD5E1]">{fmt(d.signed_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setOpenDoc(d)}
                              className="text-blue-400 hover:text-blue-300"
                              data-testid={`doc-open-${d.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredDocs.length === 0 && !loading && (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="text-center px-4 py-10 text-[#94A3B8]">
                    {isClient ? 'No documents have been sent to you yet.' : 'No documents match the current filter.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {isAdmin && showCreate && (
        <SendDocumentModal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          clients={clients}
          onSent={() => { setShowCreate(false); load(); }}
          token={token}
        />
      )}

      {isAdmin && showTemplates && (
        <TemplatesModal
          open={showTemplates}
          onClose={() => setShowTemplates(false)}
          clients={clients}
          onApplied={() => { setShowTemplates(false); load(); }}
          token={token}
        />
      )}

      {openDoc && (
        <DocumentViewer
          doc={openDoc}
          isAdmin={isAdmin}
          isClient={isClient}
          onClose={() => setOpenDoc(null)}
          onChanged={() => { setOpenDoc(null); load(); }}
          token={token}
        />
      )}
    </div>
  );
};

const SendDocumentModal = ({ open, onClose, clients, onSent, token }) => {
  const [clientId, setClientId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!clientId || !title.trim()) {
      toast({ title: 'Please pick a client and enter a title.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('client_id', clientId);
      fd.append('title', title.trim());
      fd.append('description', description);
      if (file) fd.append('file', file);
      const r = await fetch(`${BACKEND_URL}/api/client-documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail || 'Failed to send');
      }
      toast({ title: 'Document sent to client' });
      onSent();
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131A2A] border-[#334155] max-w-lg" data-testid="send-document-modal">
        <DialogHeader><DialogTitle className="text-[#F8FAFC]">Send Document to Client</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="modal-client-select">
                <SelectValue placeholder="Pick a client" />
              </SelectTrigger>
              <SelectContent className="bg-[#131A2A] border-[#334155] max-h-[220px]">
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">
                    {c.name || c.full_name || c.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Retainer Agreement" className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="modal-title-input" />
          </div>
          <div>
            <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" />
          </div>
          <div>
            <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Attachment (optional)</Label>
            <div className="flex items-center gap-2">
              <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="block w-full text-sm text-[#CBD5E1] file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#1D4ED8] file:text-white file:cursor-pointer"
                     data-testid="modal-file-input" />
            </div>
            {file && <p className="text-xs text-[#94A3B8] mt-1">Selected: {file.name}</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="border-[#334155] text-[#94A3B8]">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="modal-submit-btn">
            <Send className="w-4 h-4 mr-2" /> {saving ? 'Sending…' : 'Send Document'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DocumentViewer = ({ doc, isAdmin, isClient, onClose, onChanged, token }) => {
  const [typedName, setTypedName] = useState('');
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [tab, setTab] = useState('review');
  // Iter138b — admin countersign inline UI
  const [adminTypedName, setAdminTypedName] = useState('');
  const [adminAck, setAdminAck] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);

  // Iter138 state machine: admin must sign first (status PENDING -> ADMIN_SIGNED),
  // then the client sees + signs (ADMIN_SIGNED -> SIGNED).
  const canAdminSign = isAdmin && doc.status === 'PENDING' && !doc.admin_signed_at;
  const canSign = isClient && doc.status === 'ADMIN_SIGNED';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const adminSign = async () => {
    if (!adminAck || adminTypedName.trim().length < 3) return;
    setAdminSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/client-documents/${doc.id}/admin-sign`, {
        method: 'POST', headers,
        body: JSON.stringify({ typed_name: adminTypedName.trim(), acknowledged: true }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d?.detail || 'Admin sign failed'); }
      toast({ title: 'Countersigned', description: 'Client has been notified and may now sign.' });
      onChanged();
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setAdminSaving(false); }
  };

  const sign = async () => {
    if (!ack || typedName.trim().length < 3) return;
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/client-documents/${doc.id}/sign`, {
        method: 'POST', headers,
        body: JSON.stringify({ typed_name: typedName.trim(), acknowledged: true }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d?.detail || 'Sign failed'); }
      toast({ title: 'Signed', description: 'Document signed successfully.' });
      onChanged();
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast({ title: 'Please provide a reason.', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/client-documents/${doc.id}/reject`, {
        method: 'POST', headers, body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d?.detail || 'Reject failed'); }
      toast({ title: 'Rejected', description: 'Admins were notified.' });
      onChanged();
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const downloadOriginal = () => window.open(`${BACKEND_URL}/api/client-documents/${doc.id}/file?token=${token}`, '_blank');
  const downloadSigned = async () => {
    const r = await fetch(`${BACKEND_URL}/api/client-documents/${doc.id}/signed-pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast({ title: 'Download failed', variant: 'destructive' });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob); window.open(url, '_blank');
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131A2A] border-[#334155] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="document-viewer">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-400" />
            <div>
              <div>{doc.title}</div>
              <div className="text-[11px] text-[#94A3B8] font-mono mt-0.5">{doc.id}</div>
            </div>
            <Badge className={`${STATUS_STYLES[doc.status]} ml-auto`}>{doc.status}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-[#94A3B8]">Client:</span> <span className="text-[#F8FAFC]">{doc.client_name || '—'}</span></div>
            <div><span className="text-[#94A3B8]">Sent by:</span> <span className="text-[#F8FAFC]">{doc.created_by_name || '—'}</span></div>
            <div><span className="text-[#94A3B8]">Sent:</span> <span className="text-[#F8FAFC]">{fmt(doc.created_at)}</span></div>
            <div><span className="text-[#94A3B8]">Signed:</span> <span className="text-[#F8FAFC]">{fmt(doc.signed_at)}</span></div>
          </div>
          {doc.description && (
            <div className="rounded border border-[#334155] bg-[#0B0F19] p-3">
              <div className="text-[10px] uppercase text-[#94A3B8] mb-1 tracking-wider">Description</div>
              <p className="text-sm text-[#CBD5E1] whitespace-pre-wrap">{doc.description}</p>
            </div>
          )}
          {doc.file_key && (
            <Button variant="outline" onClick={downloadOriginal} className="border-[#334155] text-[#F8FAFC]" data-testid="download-original-btn">
              <Download className="w-4 h-4 mr-2" /> Open original attachment
            </Button>
          )}

          {/* Iter138b — Admin countersign block (first signature) */}
          {canAdminSign && (
            <div className="rounded border border-blue-500/30 bg-blue-500/5 p-4 space-y-3" data-testid="admin-sign-block">
              <h4 className="text-blue-200 font-semibold flex items-center gap-2">
                <FileSignature className="w-4 h-4" /> Agency countersign required
              </h4>
              <p className="text-xs text-[#CBD5E1]">
                The client cannot see or sign this document until the agency has countersigned it.
                Your typed name below becomes the agency's binding signature.
              </p>
              <label className="flex items-start gap-2 text-sm text-[#CBD5E1]">
                <Checkbox checked={adminAck} onCheckedChange={(v) => setAdminAck(!!v)} className="mt-0.5" data-testid="admin-sign-ack" />
                <span>I am authorized to sign on behalf of the agency and attest the content of this document is correct.</span>
              </label>
              <Input value={adminTypedName} onChange={(e) => setAdminTypedName(e.target.value)} placeholder="Type your full legal name"
                     className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="admin-sign-name" />
              {adminTypedName && (
                <div className="p-3 bg-white rounded">
                  <div className="text-[10px] uppercase text-slate-500 tracking-wider">Agency Signature</div>
                  <div className="text-slate-900 text-2xl font-semibold" style={{ fontFamily: 'cursive' }}>{adminTypedName}</div>
                </div>
              )}
              <Button onClick={adminSign} disabled={!adminAck || adminTypedName.trim().length < 3 || adminSaving}
                      className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="admin-sign-submit">
                <FileSignature className="w-4 h-4 mr-2" /> {adminSaving ? 'Signing…' : 'Countersign as Agency'}
              </Button>
            </div>
          )}

          {/* Iter138 — Show agency signature once admin has signed */}
          {doc.admin_signed_at && doc.admin_signature && (
            <div className="rounded border border-blue-500/30 bg-blue-500/5 p-3" data-testid="agency-signature-block">
              <div className="text-[10px] uppercase text-blue-300 tracking-wider mb-1">Agency Countersigned</div>
              <div className="text-slate-100 text-xl font-semibold" style={{ fontFamily: 'cursive' }}>{doc.admin_signature.typed_name}</div>
              <div className="text-[11px] text-[#94A3B8] mt-1">
                {fmt(doc.admin_signed_at)} · hash <span className="font-mono">{(doc.admin_signature.hash || '').slice(0, 16)}…</span>
              </div>
            </div>
          )}

          {/* Admin waiting for client */}
          {isAdmin && doc.status === 'ADMIN_SIGNED' && (
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-200">
              <Clock className="w-4 h-4 inline mr-1" />
              Awaiting the client's signature. Use "Send Signing Invite" below to re-notify them.
            </div>
          )}

          {/* Client waiting for admin */}
          {isClient && doc.status === 'PENDING' && !doc.admin_signed_at && (
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-200">
              <Clock className="w-4 h-4 inline mr-1" />
              This document is being prepared by the agency. You'll be notified the moment it's ready to sign.
            </div>
          )}

          {canSign && (
            <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
              <h4 className="text-emerald-200 font-semibold flex items-center gap-2">
                <FileSignature className="w-4 h-4" /> Sign this document
              </h4>
              <label className="flex items-start gap-2 text-sm text-[#CBD5E1]">
                <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} className="mt-0.5" data-testid="viewer-ack" />
                <span>I have reviewed this document and my typed name below is my legal signature under the E-SIGN Act.</span>
              </label>
              <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Type your full legal name" className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="viewer-typed-name" />
              {typedName && (
                <div className="p-3 bg-white rounded">
                  <div className="text-[10px] uppercase text-slate-500 tracking-wider">Electronic Signature</div>
                  <div className="text-slate-900 text-2xl font-semibold" style={{ fontFamily: 'cursive' }}>{typedName}</div>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={sign} disabled={!ack || typedName.trim().length < 3 || saving}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="viewer-sign-btn">
                  <FileSignature className="w-4 h-4 mr-2" /> Sign &amp; Submit
                </Button>
                <Button onClick={() => setTab(tab === 'reject' ? 'review' : 'reject')} variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10">
                  <X className="w-4 h-4 mr-2" /> Reject
                </Button>
              </div>
              {tab === 'reject' && (
                <div className="space-y-2 pt-3 border-t border-red-500/20">
                  <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Reason for rejection</Label>
                  <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
                            className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                            data-testid="viewer-reject-reason" />
                  <Button onClick={reject} disabled={saving || !rejectReason.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="viewer-reject-confirm">Confirm rejection</Button>
                </div>
              )}
            </div>
          )}

          {doc.status === 'SIGNED' && doc.signature && (
            <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="text-[10px] uppercase text-emerald-300 tracking-wider mb-1">Signed by</div>
              <div className="text-slate-100 text-2xl font-semibold" style={{ fontFamily: 'cursive' }}>{doc.signature.typed_name}</div>
              <div className="text-[11px] text-[#94A3B8] mt-1">
                {fmt(doc.signature.signed_at)} · hash <span className="font-mono">{(doc.signature.hash || '').slice(0, 16)}…</span>
              </div>
            </div>
          )}
          {doc.status === 'REJECTED' && (
            <div className="rounded border border-red-500/30 bg-red-500/5 p-4 text-sm">
              <div className="text-red-300 font-medium">Rejected by client</div>
              <div className="text-[#CBD5E1] mt-1">{doc.rejection_reason || '—'}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[#334155]">
            {isAdmin && doc.status === 'PENDING' && (
              <Button onClick={async () => {
                const r = await fetch(`${BACKEND_URL}/api/client-documents/${doc.id}/invite`, {
                  method: 'POST', headers, body: JSON.stringify({ send_email: true, send_push: true }),
                });
                const d = await r.json().catch(() => ({}));
                if (!r.ok) { toast({ title: 'Failed', description: d?.detail || '', variant: 'destructive' }); return; }
                const em = d.email?.success ? 'email\u2713' : (doc.client_email ? 'email\u2717' : 'no email');
                const push = d.push?.success ? 'push\u2713' : 'push\u2013';
                toast({ title: 'Invite sent', description: `${em} \u00b7 ${push} \u00b7 link valid 7 days` });
              }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-invite-btn">
                <Mail className="w-4 h-4 mr-2" /> Send Signing Invite
              </Button>
            )}
            <Button variant="outline" onClick={downloadSigned} className="border-[#334155] text-[#F8FAFC]" data-testid="download-signed-btn">
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
            <Button variant="ghost" onClick={onClose} className="text-[#94A3B8]">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TemplatesModal = ({ open, onClose, clients, onApplied, token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, name, title, description, body, kind}
  const [applyState, setApplyState] = useState({ open: false, template: null, clientId: '' });

  const empty = { name: '', title: '', description: '', body: '', kind: 'general' };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/client-documents/templates`, { headers });
      if (r.ok) setTemplates(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!editing?.name?.trim() || !editing?.title?.trim()) {
      toast({ title: 'Template name and title are required.', variant: 'destructive' });
      return;
    }
    const method = editing.id ? 'PATCH' : 'POST';
    const url = editing.id
      ? `${BACKEND_URL}/api/client-documents/templates/${editing.id}`
      : `${BACKEND_URL}/api/client-documents/templates`;
    const r = await fetch(url, {
      method, headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(editing),
    });
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast({ title: 'Save failed', description: d?.detail || '', variant: 'destructive' }); return; }
    toast({ title: 'Template saved' });
    setEditing(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    const r = await fetch(`${BACKEND_URL}/api/client-documents/templates/${id}`, { method: 'DELETE', headers });
    if (r.ok) { toast({ title: 'Template deleted' }); load(); }
  };

  const apply = async () => {
    if (!applyState.clientId) { toast({ title: 'Pick a client to apply this template.', variant: 'destructive' }); return; }
    const r = await fetch(`${BACKEND_URL}/api/client-documents/from-template`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ template_id: applyState.template.id, client_id: applyState.clientId }),
    });
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast({ title: 'Failed', description: d?.detail || '', variant: 'destructive' }); return; }
    toast({ title: 'Document sent from template' });
    setApplyState({ open: false, template: null, clientId: '' });
    onApplied();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131A2A] border-[#334155] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="templates-modal">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" /> Document Templates
          </DialogTitle>
          <p className="text-xs text-[#94A3B8]">Save common documents once (retainer, disclosure, ATA cover) and send them to any client with a single click.</p>
        </DialogHeader>
        {editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Internal name *</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                       className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="tpl-name" />
              </div>
              <div>
                <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Kind</Label>
                <Select value={editing.kind} onValueChange={(v) => setEditing({ ...editing, kind: v })}>
                  <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="tpl-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131A2A] border-[#334155]">
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="retainer">Retainer</SelectItem>
                    <SelectItem value="disclosure">Disclosure</SelectItem>
                    <SelectItem value="ata_cover">ATA Cover</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Document title *</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                     className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="tpl-title" />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Description</Label>
              <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                        className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Body</Label>
              <Textarea rows={8} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                        placeholder="Full document body — appears on the signing page and in the exported PDF."
                        className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="tpl-body" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)} className="border-[#334155] text-[#94A3B8]">Cancel</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tpl-save-btn">
                {editing.id ? 'Save changes' : 'Create template'}
              </Button>
            </div>
          </div>
        ) : applyState.open ? (
          <div className="space-y-3">
            <div className="rounded border border-[#334155] bg-[#0B0F19] p-3">
              <div className="text-[10px] uppercase text-[#94A3B8] tracking-wider mb-1">Template</div>
              <div className="text-[#F8FAFC] font-semibold">{applyState.template.title}</div>
              <div className="text-xs text-[#94A3B8] mt-1">{applyState.template.description}</div>
            </div>
            <div>
              <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Send to client</Label>
              <Select value={applyState.clientId} onValueChange={(v) => setApplyState({ ...applyState, clientId: v })}>
                <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="tpl-apply-client">
                  <SelectValue placeholder="Choose a client" />
                </SelectTrigger>
                <SelectContent className="bg-[#131A2A] border-[#334155] max-h-[220px]">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">
                      {c.name || c.full_name || c.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setApplyState({ open: false, template: null, clientId: '' })} className="border-[#334155] text-[#94A3B8]">Cancel</Button>
              <Button onClick={apply} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tpl-apply-btn">
                <Zap className="w-4 h-4 mr-2" /> Send now
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-3">
              <div className="text-sm text-[#94A3B8]">{templates.length} saved template{templates.length === 1 ? '' : 's'}</div>
              <Button onClick={() => setEditing(empty)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="tpl-new-btn">
                <Plus className="w-4 h-4 mr-2" /> New template
              </Button>
            </div>
            <div className="space-y-2">
              {loading && <div className="text-[#94A3B8] text-sm">Loading…</div>}
              {!loading && templates.length === 0 && (
                <div className="text-[#94A3B8] text-sm py-8 text-center border border-dashed border-[#334155] rounded">
                  No templates yet. Save your first reusable document above.
                </div>
              )}
              {templates.map((t) => (
                <div key={t.id} className="rounded border border-[#334155] bg-[#0B0F19] p-3 flex items-start justify-between gap-3" data-testid={`tpl-row-${t.id}`}>
                  <div className="min-w-0">
                    <div className="text-[#F8FAFC] font-semibold truncate">{t.title}</div>
                    <div className="text-[10px] uppercase text-[#94A3B8] tracking-wider mt-0.5">{t.kind} · {t.name}</div>
                    {t.description && <div className="text-xs text-[#CBD5E1] mt-1 line-clamp-2">{t.description}</div>}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button size="sm" onClick={() => setApplyState({ open: true, template: t, clientId: '' })}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-3" data-testid={`tpl-use-${t.id}`}>
                      <Zap className="w-3 h-3 mr-1" /> Use
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(t)}
                            className="border-[#334155] text-[#F8FAFC] h-7 px-3">Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => del(t.id)}
                            className="text-red-400 h-7 px-3">Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientDocuments;